import { Sound } from './audio/audio';
import { cueFor } from './audio/cues';
import { Loop } from './core/loop';
import { Input } from './input/input';
import { GAME_TAGLINE, GAME_TITLE, drawFlame, drawIntro, installFavicon } from './render/intro';
import { Renderer } from './render/renderer';
import { DIFFICULTIES, type Difficulty } from './sim/difficulty';
import {
  EState,
  TACTICS,
  engineStatus,
  issueOrder,
  namedObjectives,
  resolveObjective,
  tacticRefusal,
  type Tactic,
} from './sim/engine';
import { CELL, LOGICAL_H, LOGICAL_W } from './sim/types';
import { World } from './sim/world';
import { Hud } from './ui/hud';

const canvas = document.getElementById('screen') as HTMLCanvasElement;
const ctx = canvas.getContext('2d', { alpha: false })!;

const renderer = new Renderer(ctx);
const input = new Input();
const hud = new Hud();
const sound = new Sound();

const IDLE_INPUT = { up: false, down: false, left: false, right: false, interact: false, drop: false, swap: false };
const SPEEDS = [1, 2, 4];

let world: World;
let current: Difficulty = DIFFICULTIES[0];
let currentSeed = 1;
/** True while the menu is up: the mountain burns behind it, with nobody fighting it. */
let attract = true;
let paused = false;
let speedIndex = 0;
let muted = false;
let seenMessages = 0;
let seenRescued = 0;
let seenSafe = 0;
let seenSinceDrop = 99;

// ---------------------------------------------------------------------------
// Layout: integer-scale the pixel canvas into whatever space the window has.
// ---------------------------------------------------------------------------
/** The map now fills the window; the panels float on top of it. */
function layout(): void {
  const raw = Math.min(window.innerWidth / LOGICAL_W, window.innerHeight / LOGICAL_H);
  const scale = raw >= 1 ? Math.floor(raw * 2) / 2 : Math.max(0.4, raw);
  canvas.style.width = `${Math.round(LOGICAL_W * scale)}px`;
  canvas.style.height = `${Math.round(LOGICAL_H * scale)}px`;
}
window.addEventListener('resize', layout);

// ---------------------------------------------------------------------------
// Game lifecycle
// ---------------------------------------------------------------------------
function newIncident(d: Difficulty, seed: number, playing: boolean): void {
  current = d;
  currentSeed = seed;
  world = new World(d, seed);
  attract = !playing;
  paused = false;
  speedIndex = 0;
  loop.speed = SPEEDS[speedIndex];
  seenMessages = world.messages.length;
  seenRescued = 0;
  seenSafe = 0;
  hud.armed = null;
  renderer.aim = null;
  hud.showDash(false);
  if (playing) hud.hide();
}

// ---------------------------------------------------------------------------
// Commanding the ground units: pick a unit, arm a tactic, click an objective.
// ---------------------------------------------------------------------------
hud.onSelect = (id) => {
  world.selected = world.selected === id ? -1 : id;
  hud.armed = null;
  sound.blip();
};
hud.onHold = (id) => {
  const e = world.engines[id];
  if (!e?.order) return;
  e.order.hold = true;
  e.warnTimer = Infinity;
  world.say(`${e.name} holding position on your order. Watch their road.`, 'warn');
};
/** An order given from the dashboard, against a named objective. */
hud.onOrder = (id, tactic, obj) => {
  const e = world.engines[id];
  if (!e) return;
  if (issueOrder(world, e, tactic, obj)) sound.select();
  else sound.deny();
};
hud.onResume = () => setPaused(false);

function setPaused(on: boolean): void {
  paused = on;
  loop.speed = on ? 0 : SPEEDS[speedIndex];
  hud.showDash(on);
  if (on) hud.updateDash(world);
}

function commandUnits(): void {
  // Number keys arm a tactic for the selected unit.
  TACTICS.forEach((t, i) => {
    if (input.tapped(`Digit${i + 1}`) && world.selected >= 0) {
      hud.armed = hud.armed === t.key ? null : t.key;
    }
  });
  if (input.tapped('Tab')) {
    const usable = world.engines.filter((e) => e.state !== EState.Responding && e.state !== EState.Lost);
    if (usable.length > 0) {
      const at = usable.findIndex((e) => e.id === world.selected);
      world.selected = usable[(at + 1) % usable.length].id;
      hud.armed = null;
    }
  }

  const engine = world.engines[world.selected];
  const live = engine && engine.state !== EState.Responding && engine.state !== EState.Lost;

  // Hover: show what an order here would mean before it is given.
  renderer.aim = null;
  if (input.pointer && live && hud.armed) {
    const cx = Math.floor(input.pointer.x / CELL);
    const cy = Math.floor(input.pointer.y / CELL);
    const obj = resolveObjective(world, cx, cy);
    if (obj) {
      const refusal = hud.armed === 'withdraw' ? null : tacticRefusal(world, hud.armed, obj);
      renderer.aim = { x: obj.x, y: obj.y, label: obj.label, ok: !refusal };
    }
  }

  const click = input.takeClick();
  if (!click) return;

  const cx = Math.floor(click.x / CELL);
  const cy = Math.floor(click.y / CELL);

  // Right click, or a click with nothing armed, selects the unit under it.
  const hit = world.engines.find((e) => {
    if (e.state === EState.Responding) return false;
    const road = world.roads[e.roadId];
    const c = road.cells[Math.max(0, Math.min(road.cells.length - 1, Math.round(e.s)))];
    return Math.abs(c.x - cx) <= 2 && Math.abs(c.y - cy) <= 2;
  });
  if (click.right) {
    world.selected = -1;
    hud.armed = null;
    return;
  }
  if (hit && !hud.armed) {
    world.selected = world.selected === hit.id ? -1 : hit.id;
    return;
  }
  if (!live || !hud.armed) return;

  const obj = resolveObjective(world, cx, cy);
  if (!obj) return;
  if (issueOrder(world, engine, hud.armed as Tactic, obj)) {
    hud.armed = null;
    sound.select();
  } else {
    sound.deny();
  }
}

function toMenu(): void {
  newIncident(DIFFICULTIES[1], (Math.random() * 0xffffffff) >>> 0, false);
  hud.menu((d, seed) => {
    sound.select();
    newIncident(d, seed, true);
  });
}

// ---------------------------------------------------------------------------
// Fixed-step simulation
// ---------------------------------------------------------------------------
function step(): void {
  world.input = attract ? IDLE_INPUT : input.takeHeliInput();
  world.step();
  if (attract && world.phase === 'over') {
    // Start another incident behind the menu, so there is always fire on
    // screen and sound playing.
    newIncident(DIFFICULTIES[1], (Math.random() * 0xffffffff) >>> 0, false);
  }
}

// ---------------------------------------------------------------------------
// Per-frame: UI keys, HUD, render, audio
// ---------------------------------------------------------------------------
/** True when this page is embedded in a frame on somebody else's site. */
const EMBEDDED = window.self !== window.top;

function draw(): void {
  // Audio is created at boot; browsers hold it suspended until the first real
  // interaction, so resume it as soon as one has happened.
  if (input.gestured && sound.blocked) sound.resume();

  // In an embed the keyboard belongs to the page around us until this document
  // is clicked. Say so, rather than letting the controls look broken. On the
  // game's own page this never appears, because there is no frame to lose
  // focus to. The rule everywhere else in the game: if a key will do nothing,
  // tell the player why.
  document.getElementById('focusnote')!.classList.toggle('on', EMBEDDED && !document.hasFocus());

  if (!attract) {
    if (input.tapped('KeyH')) {
      hud.tab = 'controls';
      setPaused(true);
    }
    if (input.tapped('Escape')) {
      if (paused) setPaused(false);
      else toMenu();
    }
    if (input.tapped('KeyP')) setPaused(!paused);
    if (input.tapped('Minus') && speedIndex > 0) {
      speedIndex--;
      if (!paused) loop.speed = SPEEDS[speedIndex];
    }
    if (input.tapped('Equal') && speedIndex < SPEEDS.length - 1) {
      speedIndex++;
      if (!paused) loop.speed = SPEEDS[speedIndex];
    }
    if (input.tapped('KeyM')) {
      muted = !muted;
      sound.setMuted(muted);
    }
    if (input.tapped('KeyR')) newIncident(current, currentSeed, true);
    if (input.tapped('KeyV') && world.phase === 'playing') {
      world.orderEvacuation();
      sound.alarm();
    }
    if (!paused && world.phase === 'playing') commandUnits();
  } else if (input.tapped('Escape')) {
    toMenu();
  }

  const time = performance.now() / 1000;
  renderer.draw(world, time);
  if (hud.introCtx) drawIntro(hud.introCtx, time, sound.blocked);

  if (!attract) {
    hud.update(world, paused ? 0 : SPEEDS[speedIndex], muted);
    hud.updateDash(world);
  }

  // The attract screen has its own tune and background sound. Both stop the
  // moment an incident starts, and the simulation takes back the sound beds:
  // the looping background layers that play under the game.
  if (sound.started) {
    sound.setTitleTune(attract);
    sound.titleAmbience(attract);
  }

  // Audio cues driven by what comes over the radio. The patterns live in
  // src/audio/cues.ts, where a test keeps them matching what the units say.
  for (let i = seenMessages; i < world.messages.length; i++) {
    const msg = world.messages[i];
    switch (cueFor(msg.text)) {
      case 'splash':
        sound.splash();
        sound.steam();
        break;
      case 'siren':
        sound.siren();
        break;
      case 'toll':
        sound.toll();
        break;
      case 'klaxon':
        sound.klaxon();
        break;
      case 'blip':
        sound.blip();
        break;
      default:
        if (msg.kind === 'bad') sound.alert();
    }
  }
  seenMessages = world.messages.length;

  // The bucket releasing its water.
  if (world.heli.sinceDrop < seenSinceDrop) sound.whoosh();
  seenSinceDrop = world.heli.sinceDrop;
  if (world.stats.rescued > seenRescued || world.stats.safe > seenSafe) {
    if (world.stats.rescued > seenRescued) {
      sound.cheer();
      sound.crowd();
    }
    seenRescued = world.stats.rescued;
    seenSafe = world.stats.safe;
  }
  sound.update(world);

  if (!attract && world.phase === 'over' && !document.getElementById('overlay')!.classList.contains('on')) {
    loop.speed = 0;
    if (world.result?.pass) sound.jingleWin();
    else sound.jingleFail();
    hud.result(
      world,
      () => newIncident(current, currentSeed, true),
      () => toMenu(),
    );
  }

  input.endFrame();
}

// ---------------------------------------------------------------------------
const loop = new Loop(step, draw);

// Start the audio graph immediately so the title tune can play from the start.
// It sits suspended until the browser has seen the player interact; the
// listeners below release it the moment that happens, anywhere on the page.
sound.start();
for (const ev of ['pointerdown', 'keydown'] as const) {
  window.addEventListener(ev, () => sound.resume(), { passive: true });
}

// Title and tab icon come from the one place the name is defined.
installFavicon();
document.title = `${GAME_TITLE}: ${GAME_TAGLINE}`;
{
  const bar = document.getElementById('title');
  if (bar) {
    const c = document.createElement('canvas');
    c.width = 13;
    c.height = 13;
    c.className = 'barflame';
    const fctx = c.getContext('2d');
    if (fctx) drawFlame(fctx, 0, 0, 13);
    bar.textContent = '';
    bar.appendChild(c);
    const label = document.createElement('span');
    label.textContent = GAME_TITLE;
    bar.appendChild(label);
  }
}

input.attach();
input.attachCanvas(canvas);
layout();
toMenu();
loop.start();

// Dev-only handle for poking at a running incident from the console.
if (import.meta.env.DEV) {
  (window as unknown as { FR: unknown }).FR = {
    get world() {
      return world;
    },
    get loop() {
      return loop;
    },
    start: (key = 'bombero', seed = 1234) =>
      newIncident(DIFFICULTIES.find((d) => d.key === key) ?? DIFFICULTIES[1], seed, true),
    fastForward: (seconds: number) => {
      for (let n = 0; n < seconds * 30; n++) {
        world.input = IDLE_INPUT;
        world.step();
      }
    },
    sound,
    hud,
    /** Open or close the control dashboard without needing a rendered frame. */
    pause: (on = true) => setPaused(on),
    /** Ground-unit command API, for scripting an incident from the console. */
    engines: { resolveObjective, issueOrder, tacticRefusal, engineStatus, namedObjectives, TACTICS, EState },
    /**
     * Run one frame's worth of UI work straight away: command handling, panel
     * update and render. It does not wait for requestAnimationFrame, which does
     * not fire in a hidden browser pane.
     */
    render: () => {
      const now = performance.now() / 1000;
      if (!attract && !paused && world.phase === 'playing') commandUnits();
      renderer.draw(world, now);
      if (hud.introCtx) drawIntro(hud.introCtx, now, sound.blocked);
      if (!attract) {
        hud.update(world, paused ? 0 : SPEEDS[speedIndex], muted);
        hud.updateDash(world);
      }
      input.endFrame();
    },
  };
}



